import React, { useMemo, ReactNode } from 'react';
import { SelectListItem } from '../../typings/viewModels/selectListItemViewModel';
import useTheme from '../../contexts/useTheme';
import cx from 'classnames';
import UnstyledLink from '../UnstyledLink/UnstyledLink';

export const separator = {
  value: 'separator',
  label: '',
};

export type TabbedSelectOption = SelectListItem<string | number> & {
  isDisabled?: boolean;
  isHighlighted?: boolean;
  isExternalLink?: boolean;
  badgeLabel?: string | null;
  transformLabel?: (option: string) => JSX.Element;
  isVertical?: boolean;
};

type Props<OptionType> = {
  value: OptionType | null;
  options: OptionType[];
  onChange?: (option: OptionType) => void;
  isDisabled?: boolean;
  style?: TabbedSelectStyle;
  backgroundColorLevel?: 0 | 1 | 2;
  OptionWrapper?: ({
    option,
    children,
  }: {
    option: OptionType;
    children: ReactNode;
  }) => JSX.Element | null;
  className?: string;
  wrapperInheritsProps?: boolean;
  transformLabel?: (option: OptionType) => JSX.Element;
  isVertical?: boolean;
  badgeLabelStyle?: React.CSSProperties;
};

export type TabbedSelectStyle =
  | 'default'
  | 'bubble'
  | 'rounded-square'
  | 'v2'
  | 'v3';

function DefaultOptionWrapper({
  children,
}: {
  children: ReactNode;
} & Record<string, unknown>) {
  return <>{children}</>;
}

function TabbedSelect<OptionType extends TabbedSelectOption>({
  value,
  options,
  onChange = () => null,
  isDisabled = false,
  style,
  backgroundColorLevel = 0,
  OptionWrapper = DefaultOptionWrapper,
  className,
  wrapperInheritsProps,
  transformLabel,
  isVertical = false,
  badgeLabelStyle,
}: Props<OptionType>): JSX.Element {
  const theme = useTheme();
  style = style ?? theme.tabbedSelectStyle;

  return (
    <div
      className={`tabbed-select tabbed-select--${style} ${
        backgroundColorLevel
          ? `tabbed-select--level-${backgroundColorLevel}`
          : ''
      } ${options.length <= 3 && !isVertical ? 'tabbed-select--short' : ''} ${
        className ? className : ''
      } ${
        isVertical
          ? `tabbed-select--vertical tabbed-select--${style}-vertical`
          : ''
      }`}
    >
      {options.map((option, index) =>
        option === separator ? (
          <div key={index} className={`tabbed-select__separator--${style}`} />
        ) : (
          <TabbedSelectOption
            key={option.value}
            option={option}
            value={value}
            onChange={onChange}
            style={style}
            isDisabled={isDisabled}
            OptionWrapper={OptionWrapper}
            wrapperInheritsProps={wrapperInheritsProps}
            transformLabel={transformLabel}
            isVertical={isVertical}
            badgeLabelStyle={badgeLabelStyle}
          />
        )
      )}
    </div>
  );
}

function TabbedSelectOption<OptionType extends TabbedSelectOption>({
  option,
  value,
  onChange = () => null,
  style,
  isDisabled,
  OptionWrapper = DefaultOptionWrapper,
  wrapperInheritsProps,
  transformLabel,
  isVertical = false,
  badgeLabelStyle,
}: Pick<
  Props<OptionType>,
  | 'value'
  | 'onChange'
  | 'style'
  | 'isDisabled'
  | 'OptionWrapper'
  | 'wrapperInheritsProps'
  | 'transformLabel'
  | 'isVertical'
  | 'badgeLabelStyle'
> & {
  option: OptionType;
}) {
  const optionIsDisabled = isDisabled || option.isDisabled;

  const Wrapper = useMemo(
    () =>
      function Wrapper({
        children,
        ...rest
      }: { children: ReactNode } & Record<string, unknown>) {
        return (
          <OptionWrapper option={option} {...rest}>
            {children}
          </OptionWrapper>
        );
      },
    [OptionWrapper, option]
  );

  const label = useMemo(
    () => (transformLabel ? transformLabel(option) : option.label),
    [option, transformLabel]
  );

  const children = (
    <>
      <span className={'tabbed-select__bullet-point'}>•</span>
      {option.iconUrl?.length && (
        <img
          className={'tabbed-select__tab-icon'}
          src={option.iconUrl}
          alt={''}
        />
      )}
      <span
        className={cx({
          'tabbed-select__tab-label': true,
          'tabbed-select__tab-label--selected': option.value === value?.value,
        })}
      >
        {label}
        {option.badgeLabel && (
          <div
            className={'tabbed-select__tab-badge-label'}
            style={badgeLabelStyle}
          >
            {option.badgeLabel}
          </div>
        )}
      </span>
      {option.isExternalLink && <i className={'zmdi zmdi-open-in-new'} />}
    </>
  );

  const className = `tabbed-select__tab tabbed-select__tab--${style} ${
    option.value === value?.value
      ? `tabbed-select__tab--selected tabbed-select__tab--${style}-selected`
      : ''
  } ${
    option.isHighlighted
      ? `tabbed-select__tab--highlighted tabbed-select__tab--${style}-highlighted`
      : ''
  } ${
    isVertical
      ? `tabbed-selected__tab--vertical tabbed-select__tab--${style}-vertical`
      : ''
  }`;

  const forwardedHref =
    wrapperInheritsProps && option.url ? option.url : undefined;
  const forwardedClassName =
    wrapperInheritsProps && option.url ? className : undefined;

  if (option.url) {
    return (
      <Wrapper href={forwardedHref} className={forwardedClassName}>
        {wrapperInheritsProps ? (
          children
        ) : (
          <UnstyledLink
            href={option.url}
            disableDefaultLinkUnstyling={true}
            className={className}
          >
            {children}
          </UnstyledLink>
        )}
      </Wrapper>
    );
  }

  return (
    <Wrapper href={forwardedHref} className={forwardedClassName}>
      <button
        className={className}
        onClick={
          option.url
            ? undefined
            : () =>
                !optionIsDisabled &&
                option.value !== value?.value &&
                onChange(option)
        }
        disabled={optionIsDisabled}
      >
        {children}
      </button>
    </Wrapper>
  );
}

export default TabbedSelect;
